import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Building2, FolderKanban, CheckCircle2, Clock, AlertTriangle,
  Users, BarChart3, ChevronRight, Activity, FileText, IndianRupee, ClipboardCheck
} from 'lucide-react';
import VendorTasksPage from '@/pages/VendorTasksPage';
import AnimatorAllotmentPage from '@/pages/AnimatorAllotmentPage';
import RevisionsInboxWidget from '@/components/RevisionsInboxWidget';

/**
 * VendorDashboard — landing dashboard for vendor_producer accounts.
 *
 * Three top-level "views" via the tab bar:
 *   • dashboard — stats overview
 *   • projects  — list of projects this vendor is on
 *   • workorders — WO list with accept buttons
 *   • status    — embedded VendorTasksPage (per-task sub-allot picker)
 *
 * Founder consistency fix Feb 14, 2026: the "Current Status" tab used
 * to render an artist-style kanban (In Progress / Pending / Done with
 * Start Working / Submit for Review / Mark Done buttons). That UX is
 * for individual artists who DO the work themselves; a vendor producer
 * MANAGES a team, never personally clicks Start Working. The screenshot
 * of Mukesh's dashboard showed him stuck on that wrong-template view
 * while Soniya's "My Tasks" sidebar link took her to the correct
 * sub-allotment flow (VendorTasksPage). Result: one vendor sub-allotted
 * to her team while another couldn't, because the OS exposed two
 * different paths to two vendor_producers. Now both lead to the same
 * VendorTasksPage which has the canonical "Assign to Team Artist" picker.
 */
export default function VendorDashboard() {
  const { user, api } = useAuth();
  const [view, setView] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [vendorTeam, setVendorTeam] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [tasks, setTasks] = useState({ total: 0, completed: 0, in_progress: 0 });

  useEffect(() => {
    Promise.all([
      api.get('/dashboard/stats').then(r => setStats(r.data)).catch(() => {}),
      api.get('/projects').then(r => setProjects(r.data || [])).catch(() => {}),
      api.get('/vendor/my-team').then(r => setVendorTeam(r.data || [])).catch(() => setVendorTeam([])),
      api.get('/work-orders').then(r => setWorkOrders(r.data || [])).catch(() => setWorkOrders([])),
    ]);
  }, [api]);

  useEffect(() => {
    if (!stats) return;
    setTasks({
      total: stats.total_tasks || 0,
      completed: stats.completed_tasks || 0,
      in_progress: stats.in_progress_tasks || 0,
    });
  }, [stats]);

  if (!stats) return <div className="flex items-center justify-center h-64"><div className="animate-pulse-slow text-zinc-600 text-sm">Loading...</div></div>;

  const accepted = workOrders.filter(w => w.status === 'accepted').length;
  const pendingAccept = workOrders.filter(w => w.status === 'issued').length;

  return (
    <div className="space-y-4" data-testid="vendor-dashboard">
      {/* Header */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-zinc-500 uppercase tracking-wider">Welcome back</p>
            <h1 className="text-2xl font-heading font-bold text-white mt-1">{user?.full_name}</h1>
            <p className="text-xs text-zinc-500 mt-1 flex items-center gap-2">
              <Building2 className="w-3 h-3" />
              {user?.email}
              <span className="text-zinc-700">·</span>
              <span className="px-1.5 py-0.5 bg-violet-500/15 text-violet-400 rounded text-[9px] uppercase">Vendor Producer</span>
            </p>
          </div>
          <div className="text-right">
            <p className="text-[10px] text-zinc-500 uppercase">Team Strength</p>
            <p className="text-3xl font-heading font-bold text-violet-400">{vendorTeam.length}</p>
          </div>
        </div>
      </div>

      {/* View tabs */}
      <div className="flex gap-1 border-b border-zinc-800">
        {[
          { key: 'dashboard', label: 'Dashboard', icon: BarChart3 },
          { key: 'projects', label: `My Projects (${projects.length})`, icon: FolderKanban },
          { key: 'workorders', label: `Work Orders (${pendingAccept ? `${pendingAccept} pending · ` : ''}${accepted})`, icon: FileText },
          { key: 'allotment', label: 'Shot Allotment', icon: ClipboardCheck },
          { key: 'status', label: 'Current Status', icon: Activity },
        ].map(t => (
          <button key={t.key} onClick={() => setView(t.key)} data-testid={`vendor-view-${t.key}`}
            className={`px-4 py-2.5 text-xs font-medium border-b-2 -mb-px flex items-center gap-1.5 ${view === t.key ? 'border-violet-500 text-violet-300' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
            <t.icon className="w-3 h-3" /> {t.label}
          </button>
        ))}
      </div>

      {/* DASHBOARD */}
      {view === 'dashboard' && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatTile icon={FolderKanban} label="Active Projects" value={stats.active_projects ?? projects.length} sub={`${projects.length} total`} color="#3b82f6" />
          <StatTile icon={Users} label="Team Size" value={vendorTeam.length} sub="invited artists" color="#8b5cf6" />
          <StatTile icon={Clock} label="In Progress" value={tasks.in_progress} sub={`${tasks.total} total`} color="#f59e0b" />
          <StatTile icon={CheckCircle2} label="Completed" value={tasks.completed} sub="this month" color="#10b981" />
        </div>
      )}

      {/* Revisions inbox — surfaces CG Sup pin-notes so vendor producers
          actually see what artists need to fix. Shown on the main
          dashboard tab AND under Current Status. */}
      {view === 'dashboard' && (
        <div className="mb-6" data-testid="vendor-dashboard-revisions">
          <RevisionsInboxWidget />
        </div>
      )}

      {/* PROJECTS */}
      {view === 'projects' && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
          {projects.length === 0 && (
            <div className="px-4 py-10 text-center text-xs text-zinc-500">
              You haven&apos;t been added to any projects yet.
            </div>
          )}
          {projects.map(p => (
            <div key={p.id} className="px-4 py-3 border-b border-zinc-800/40 flex items-center justify-between hover:bg-zinc-800/20">
              <div>
                <p className="text-xs text-zinc-200">{p.name}</p>
                <p className="text-[10px] text-zinc-500">{p.description?.slice(0, 80) || 'No description'}</p>
              </div>
              <ChevronRight className="w-3.5 h-3.5 text-zinc-600" />
            </div>
          ))}
        </div>
      )}

      {/* WORK ORDERS */}
      {view === 'workorders' && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
          {workOrders.length === 0 && (
            <div className="px-4 py-10 text-center text-xs text-zinc-500">No work orders yet.</div>
          )}
          {workOrders.map(w => (
            <div key={w.id} className="px-4 py-3 border-b border-zinc-800/40">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs text-zinc-200 truncate">
                    <span className="font-mono text-blue-400 mr-2">{w.work_order_no || w.wo_no}</span>
                    {w.project_name || w.project_id?.slice(0, 8)}
                  </p>
                  <p className="text-[10px] text-zinc-500 mt-0.5">
                    {(w.scope_type || w.kind || 'work').replace(/_/g, ' ')}
                    {w.total_amount > 0 && (
                      <span className="ml-2 text-emerald-400 font-mono">
                        <IndianRupee className="w-2.5 h-2.5 inline" />
                        {Number(w.total_amount).toLocaleString('en-IN')}
                      </span>
                    )}
                  </p>
                </div>
                <span className={`text-[9px] px-2 py-1 rounded font-bold uppercase ${
                  w.status === 'accepted' ? 'bg-emerald-500/15 text-emerald-300' :
                  w.status === 'issued' ? 'bg-amber-500/20 text-amber-300' :
                  'bg-zinc-800 text-zinc-400'}`}>
                  {w.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* SHOT ALLOTMENT — same full Animator Allotment Sheet that
          freelancers and employees see (founder ask May 14, 2026:
          "Vendor Producer dashboard doesn't show the shot details
          table that Freelancers and Employees see — include it"). */}
      {view === 'allotment' && (
        <div data-testid="vendor-shot-allotment-tab">
          <AnimatorAllotmentPage />
        </div>
      )}

      {/* CURRENT STATUS — embedded VendorTasksPage with proper sub-allot UI. */}
      {view === 'status' && (
        <div data-testid="vendor-current-status-tab">
          {/* Quick stats summary preserved above the embedded page. */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <StatTile icon={FolderKanban} label="Total Assigned" value={tasks.total} sub="across all WOs" color="#64748b" />
            <StatTile icon={Clock} label="In Progress" value={tasks.in_progress} sub="being worked" color="#3b82f6" />
            <StatTile icon={CheckCircle2} label="Completed" value={tasks.completed} sub="ready for review" color="#10b981" />
            <StatTile icon={AlertTriangle} label="Blocked / Pending" value={Math.max(0, tasks.total - tasks.completed - tasks.in_progress)} sub="need allotment" color="#f59e0b" />
          </div>

          <p className="text-[10px] text-amber-400 flex items-center gap-1 mb-3">
            <Activity className="w-3 h-3" />
            Every status update flows instantly to the VawWorld production team. Use the picker
            on each task to sub-allot to a team artist.
          </p>

          {/* Pin-note revisions surfaced here too — most vendor producers
              live in the Current Status tab so we mirror the widget. */}
          <div className="mb-4">
            <RevisionsInboxWidget />
          </div>

          {/* Canonical vendor sub-allot UI — single source of truth. */}
          <VendorTasksPage />
        </div>
      )}
    </div>
  );
}

function StatTile({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
      <div className="flex items-center justify-between">
        <p className="text-[10px] text-zinc-500 uppercase tracking-wider">{label}</p>
        <Icon className="w-3.5 h-3.5" style={{ color }} />
      </div>
      <p className="text-2xl font-heading font-bold text-white mt-1">{value}</p>
      <p className="text-[10px] text-zinc-600 mt-0.5">{sub}</p>
    </div>
  );
}
